import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, MessageSquare, Send, Eye, AlertCircle, CheckCircle2,
  ToggleLeft, ToggleRight, Save, Zap,
} from "lucide-react";
import { useT } from "@/lib/i18n";

const StatCard = ({ label, value, accent = "", testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <div className={`mt-1 text-3xl font-serif-display ${accent}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
  </div>
);

const PlaceholderChip = ({ name, onClick }) => (
  <button
    onClick={onClick}
    className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#FDFBF7] border border-[#E5E5E5] text-[#C4A252] font-semibold hover:border-[#C4A252] transition"
    type="button"
    data-testid={`placeholder-${name}`}
  >
    {"{{"}{name}{"}}"}
  </button>
);

const SmsWorkflowCard = ({ wf, twilioConfigured, onUpdated }) => {
  const t = useT();
  const [draft, setDraft] = useState({ template_en: wf.template_en, template_es: wf.template_es, enabled: wf.enabled });
  const [busy, setBusy] = useState(false);
  const [previewLang, setPreviewLang] = useState("en");
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [showTest, setShowTest] = useState(false);

  useEffect(() => {
    setDraft({ template_en: wf.template_en, template_es: wf.template_es, enabled: wf.enabled });
  }, [wf]);

  const dirty = useMemo(() => (
    draft.template_en !== wf.template_en ||
    draft.template_es !== wf.template_es ||
    draft.enabled !== wf.enabled
  ), [draft, wf]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/sms-workflows/${wf.key}`, draft);
      toast.success(t("admin.sms.toast.saved"));
      onUpdated?.();
    } catch { toast.error(t("admin.tasks.toast.save_failed")); }
    finally { setBusy(false); }
  };

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      const next = !draft.enabled;
      await api.patch(`/admin/sms-workflows/${wf.key}`, { enabled: next });
      setDraft((d) => ({ ...d, enabled: next }));
      toast.success(next ? t("admin.sms.toast.enabled") : t("admin.sms.toast.disabled"));
      onUpdated?.();
    } catch { toast.error(t("admin.toast.failed")); }
    finally { setBusy(false); }
  };

  const renderPreview = async (lang) => {
    setPreviewLang(lang);
    try {
      if (dirty) {
        await api.patch(`/admin/sms-workflows/${wf.key}`, {
          template_en: draft.template_en,
          template_es: draft.template_es,
        });
        onUpdated?.();
      }
      const { data } = await api.post(`/admin/sms-workflows/${wf.key}/preview`, { lang });
      setPreview(data);
    } catch { toast.error(t("admin.sms.toast.preview_failed")); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error(t("admin.sms.toast.enter_phone")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sms-workflows/${wf.key}/send-test`, { to: testTo.trim(), lang: previewLang });
      if (data.mocked) {
        toast.success(t("admin.sms.toast.test_mocked"));
      } else {
        toast.success(`${t("admin.sms.toast.test_sent")} ${data.to}`);
      }
      setShowTest(false);
      onUpdated?.();
    } catch (err) { toast.error(err.response?.data?.detail || t("admin.sms.toast.send_failed")); }
    finally { setBusy(false); }
  };

  const insertPlaceholder = (field, name) => {
    setDraft((d) => ({ ...d, [field]: (d[field] || "") + "{{" + name + "}}" }));
  };

  return (
    <div className="border border-[#E5E5E5] bg-white p-5" data-testid={`sms-wf-${wf.key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-serif-display text-xl">{wf.name}</h3>
            {draft.enabled ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white bg-[#15803D] px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> {t("admin.sms.live")}</span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-[#737373] border border-[#E5E5E5] px-1.5 py-0.5">{t("admin.sms.disabled")}</span>
            )}
          </div>
          <div className="text-sm text-[#737373] mt-1">{wf.description}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#C4A252] mt-1 inline-flex items-center gap-1">
            <Zap className="w-3 h-3" /> {wf.trigger_label}
          </div>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={busy}
          className="text-[#0A0A0A] hover:text-[#8B1C22] transition"
          title={draft.enabled ? t("admin.sms.toggle_disable") : t("admin.sms.toggle_enable")}
          data-testid={`sms-wf-toggle-${wf.key}`}
        >
          {draft.enabled ? <ToggleRight className="w-9 h-9 text-[#15803D]" /> : <ToggleLeft className="w-9 h-9" />}
        </button>
      </div>

      <div className="mb-2">
        <div className="rn3-eyebrow mb-1.5">{t("admin.sms.placeholders")}</div>
        <div className="flex flex-wrap gap-1">
          {(wf.placeholders || []).map((p) => (
            <PlaceholderChip key={p} name={p} onClick={() => insertPlaceholder(previewLang === "es" ? "template_es" : "template_en", p)} />
          ))}
        </div>
        <div className="text-[10px] text-[#737373] mt-1">{t("admin.sms.click_chip").replace("{lang}", previewLang === "es" ? t("common.spanish") : t("common.english"))}</div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="rn3-eyebrow block mb-1.5">{t("admin.sms.template")} · {t("common.english")}</label>
          <textarea
            rows={4}
            value={draft.template_en || ""}
            onChange={(e) => setDraft({ ...draft, template_en: e.target.value })}
            className="rn3-input w-full text-sm"
            data-testid={`sms-wf-template-en-${wf.key}`}
          />
          <div className="text-[10px] text-[#737373] mt-1">{(draft.template_en || "").length} {t("admin.sms.chars")} · {Math.max(1, Math.ceil(((draft.template_en || "").length || 1) / 160))} {t("admin.sms.segments")}</div>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-1.5">{t("admin.sms.template")} · Español</label>
          <textarea
            rows={4}
            value={draft.template_es || ""}
            onChange={(e) => setDraft({ ...draft, template_es: e.target.value })}
            className="rn3-input w-full text-sm"
            data-testid={`sms-wf-template-es-${wf.key}`}
          />
          <div className="text-[10px] text-[#737373] mt-1">{(draft.template_es || "").length} {t("admin.sms.chars")} · {Math.max(1, Math.ceil(((draft.template_es || "").length || 1) / 160))} {t("admin.sms.segments")}</div>
        </div>
      </div>

      {preview && (
        <div className="mt-3 border border-dashed border-[#C4A252] bg-[#FDFBF7] p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="rn3-eyebrow"><Eye className="w-3 h-3 inline mr-1" /> {t("admin.sms.preview")} · {preview.lang === "es" ? "Español" : t("common.english")}</div>
            <div className="text-[10px] text-[#737373]">{preview.char_count} {t("admin.sms.chars")} · {preview.sms_segments} {preview.sms_segments > 1 ? t("admin.sms.segments") : t("admin.sms.segment")}</div>
          </div>
          <div className="text-sm whitespace-pre-line" data-testid={`sms-wf-preview-${wf.key}`}>{preview.rendered}</div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-[#F3EFE9]">
        <div className="flex items-center gap-2">
          <button onClick={() => renderPreview("en")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`sms-wf-preview-en-${wf.key}`}>
            <Eye className="w-3 h-3" /> {t("admin.sms.preview_en")}
          </button>
          <button onClick={() => renderPreview("es")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`sms-wf-preview-es-${wf.key}`}>
            <Eye className="w-3 h-3" /> {t("admin.sms.preview_es")}
          </button>
          <button onClick={() => setShowTest((v) => !v)} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`sms-wf-test-toggle-${wf.key}`}>
            <Send className="w-3 h-3" /> {t("admin.sms.send_test")}
          </button>
        </div>
        <button onClick={save} disabled={busy || !dirty} className="rn3-btn-primary text-xs disabled:opacity-40" data-testid={`sms-wf-save-${wf.key}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t("common.save")}
        </button>
      </div>

      {showTest && (
        <div className="mt-3 border border-[#E5E5E5] bg-[#FAFAFA] p-3" data-testid={`sms-wf-test-${wf.key}`}>
          <div className="flex items-center gap-2">
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="rn3-input flex-1 text-sm"
              data-testid={`sms-wf-test-to-${wf.key}`}
            />
            <select value={previewLang} onChange={(e) => setPreviewLang(e.target.value)} className="rn3-input text-sm" data-testid={`sms-wf-test-lang-${wf.key}`}>
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>
            <button onClick={sendTest} disabled={busy || !testTo.trim()} className="rn3-btn-gold text-xs" data-testid={`sms-wf-test-send-${wf.key}`}>
              <Send className="w-3.5 h-3.5" /> {t("admin.sms.send")}
            </button>
          </div>
          {!twilioConfigured && (
            <div className="text-[10px] text-[#737373] mt-1.5">
              <AlertCircle className="w-3 h-3 inline mr-1 text-[#C4A252]" /> {t("admin.sms.twilio_not_configured")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SmsWorkflowsTab = () => {
  const t = useT();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [twilio, setTwilio] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sms-workflows");
      setItems(data.items); setTwilio(data.twilio_configured);
    } catch { toast.error(t("admin.sms.toast.load_failed")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/portal"); return; }
    load();
  }, [user, authLoading, nav, load]);

  const enabledCount = items.filter((i) => i.enabled).length;
  const totalRuns = items.reduce((s, i) => s + (i.runs_count || 0), 0);

  return (
    <div className="rn3-container py-10" data-testid="sms-workflows-tab">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="sms-back">
        <ArrowLeft className="w-3 h-3" /> {t("admin.back_to_admin")}
      </button>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow"><MessageSquare className="w-3 h-3 inline mr-1" /> {t("admin.sms.eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-2">{t("admin.sms.heading")}</h1>
        </div>
        <div>
          {twilio ? (
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#15803D] border border-[#15803D] px-2 py-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("admin.sms.twilio_live")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C4A252] border border-[#C4A252] px-2 py-1" data-testid="sms-mocked-banner">
              <AlertCircle className="w-3.5 h-3.5" /> {t("admin.sms.twilio_mocked")}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <StatCard label={t("admin.sms.stat.total")} value={items.length} testid="stat-sms-total" />
        <StatCard label={t("admin.sms.stat.enabled")} value={enabledCount} accent={enabledCount > 0 ? "text-[#15803D]" : ""} testid="stat-sms-enabled" />
        <StatCard label={t("admin.sms.stat.disabled")} value={items.length - enabledCount} testid="stat-sms-disabled" />
        <StatCard label={t("admin.sms.stat.runs")} value={totalRuns} testid="stat-sms-runs" />
      </div>

      {loading && (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C4A252]" /></div>
      )}
      {!loading && (
        <div className="grid lg:grid-cols-2 gap-4 mt-8">
          {items.map((wf) => (
            <SmsWorkflowCard key={wf.key} wf={wf} twilioConfigured={twilio} onUpdated={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SmsWorkflowsTab;
